import { Button, Callout, Card, Input } from '@apovisio/ui';
import { livekitConnectionSchema } from '@apovisio/contracts';
import type { LivekitConnection } from '@apovisio/contracts';
import {
  ControlBar,
  GridLayout,
  LiveKitRoom,
  ParticipantTile,
  RoomAudioRenderer,
  useTracks,
} from '@livekit/components-react';
import '@livekit/components-styles';
import { Track } from 'livekit-client';
import { useState } from 'react';
import type { SyntheticEvent } from 'react';
import { BrandMark } from '../app/BrandMark';
import { ApiClientError, apiFetch } from '../lib/api';

/**
 * Démonstration média locale (Sprint 1) : vérifie la chaîne navigateur → LiveKit hors parcours
 * métier. L'API refuse d'émettre un jeton si MEDIA_DEMO_ENABLED n'est pas actif.
 */
export function MediaDemoPage() {
  const [displayName, setDisplayName] = useState('');
  const [connection, setConnection] = useState<LivekitConnection | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function join(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    setPending(true);
    setError(null);
    try {
      setConnection(
        await apiFetch('/api/dev/media-demo/token', {
          method: 'POST',
          body: { displayName },
          schema: livekitConnectionSchema,
        }),
      );
    } catch (caught) {
      setError(caught instanceof ApiClientError ? caught.message : 'Erreur inattendue.');
    } finally {
      setPending(false);
    }
  }

  if (connection) {
    return (
      <LiveKitRoom
        serverUrl={connection.serverUrl}
        token={connection.token}
        connect
        video
        audio
        data-lk-theme="default"
        className="av-theme-room demo-room"
        onDisconnected={() => {
          setConnection(null);
        }}
      >
        <DemoStage />
        <RoomAudioRenderer />
        <ControlBar controls={{ chat: false, screenShare: false, leave: true }} />
      </LiveKitRoom>
    );
  }

  return (
    <main className="app-centered">
      <Card padding="lg" className="app-narrow" as="div">
        <BrandMark subtitle="Démonstration média locale" />
        <h1 className="app-title">Tester la connexion média</h1>
        <p className="av-text-secondary">
          Ouvrez cette page dans deux navigateurs pour vérifier l’audio et la vidéo via LiveKit.
          Réservée au développement.
        </p>
        {error ? <Callout tone="danger" title={error} live="alert" /> : null}
        <form className="av-stack" onSubmit={(event) => void join(event)}>
          <Input
            label="Votre nom"
            value={displayName}
            onChange={(event) => {
              setDisplayName(event.target.value);
            }}
            required
            maxLength={60}
            autoComplete="name"
          />
          <Button type="submit" size="lg" fullWidth loading={pending}>
            Rejoindre la salle de démonstration
          </Button>
        </form>
      </Card>
    </main>
  );
}

function DemoStage() {
  const tracks = useTracks([{ source: Track.Source.Camera, withPlaceholder: true }], {
    onlySubscribed: false,
  });
  return (
    <GridLayout tracks={tracks} className="demo-room__grid">
      <ParticipantTile />
    </GridLayout>
  );
}
